import { useState, useEffect } from 'react';
import { TAXONOMY, TEAM_LISTS } from '../lib/taxonomy';

const TOP_LEVEL = Object.keys(TAXONOMY);

const selectStyle = {
  padding: '0.4rem 0.6rem',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '0.9rem',
  backgroundColor: '#fff',
  cursor: 'pointer',
  minWidth: '180px',
};

const inputStyle = {
  padding: '0.4rem 0.6rem',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '0.9rem',
  minWidth: '180px',
};

// 'Specific Team' is a sentinel: replaced in the path with the actual team name.
// Path writes as NFL › Dallas Cowboys, not NFL › Specific Team › Dallas Cowboys.
function buildGenreString(sels, cus, teamSel) {
  return sels
    .map((val, i) => {
      if (val === 'Specific Team') return teamSel || null;
      if (val === 'Other' && cus[i]) return cus[i].trim();
      return val;
    })
    .filter(Boolean)
    .join(' › ');
}

export default function GenrePicker({ onChange, initialValue }) {
  const [selections, setSelections] = useState(['', '', '', '', '']);
  const [customs, setCustoms]       = useState(['', '', '', '', '']);
  const [teamSel, setTeamSel]       = useState('');

  // Pre-populate from initialValue when editing a rejected article.
  // Stringify-compare so re-renders that pass a new object reference don't re-run this.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValueKey = JSON.stringify(initialValue);

  useEffect(() => {
    if (!initialValue?.main) return;
    const newSel = ['', '', '', '', ''];
    const newCus = ['', '', '', '', ''];
    let initialTeamSel = '';

    if (TOP_LEVEL.includes(initialValue.main)) {
      newSel[0] = initialValue.main;
    } else {
      newSel[0] = 'Other';
      newCus[0] = initialValue.main;
    }

    if (initialValue.sub && newSel[0] !== 'Other') {
      const parts = initialValue.sub.split(' › ');

      if (parts[0]) {
        const t2keys = Object.keys(TAXONOMY[newSel[0]] || {});
        if (t2keys.includes(parts[0])) { newSel[1] = parts[0]; }
        else { newSel[1] = 'Other'; newCus[1] = parts[0]; }
      }

      if (parts[1] && newSel[1] && newSel[1] !== 'Other') {
        const t3data = TAXONOMY[newSel[0]]?.[newSel[1]];
        if (t3data && !Array.isArray(t3data)) {
          const t3keys = Object.keys(t3data);
          if (t3keys.includes(parts[1])) { newSel[2] = parts[1]; }
          else { newSel[2] = 'Other'; newCus[2] = parts[1]; }
        }
      }

      if (parts[2] && newSel[2] && newSel[2] !== 'Other') {
        const t4data = TAXONOMY[newSel[0]]?.[newSel[1]]?.[newSel[2]];
        if (Array.isArray(t4data)) {
          if (t4data.includes(parts[2])) { newSel[3] = parts[2]; }
          else { newSel[3] = 'Other'; newCus[3] = parts[2]; }
        } else if (t4data && typeof t4data === 'object') {
          const t4keys = Object.keys(t4data);
          if (t4keys.includes(parts[2])) { newSel[3] = parts[2]; }
          else { newSel[3] = 'Other'; newCus[3] = parts[2]; }
        }
      }

      if (parts[3] && newSel[3] && newSel[3] !== 'Other') {
        const t5data = TAXONOMY[newSel[0]]?.[newSel[1]]?.[newSel[2]]?.[newSel[3]];
        if (Array.isArray(t5data)) {
          if (t5data.includes(parts[3])) {
            newSel[4] = parts[3];
          } else if (t5data.includes('Specific Team') && TEAM_LISTS[newSel[3]]?.includes(parts[3])) {
            // Saved path had a team name — restore sentinel + team
            newSel[4] = 'Specific Team';
            initialTeamSel = parts[3];
          } else {
            newSel[4] = 'Other'; newCus[4] = parts[3];
          }
        }
      }
    }

    setSelections(newSel);
    setCustoms(newCus);
    setTeamSel(initialTeamSel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValueKey]);

  function getTier2Options() {
    const t1 = selections[0];
    if (!t1 || !TAXONOMY[t1]) return null;
    // 'Other' is already a key in every TAXONOMY[t1] object — do not append again
    return ['', ...Object.keys(TAXONOMY[t1])];
  }

  function getTier3Options() {
    const t1 = selections[0];
    const t2 = selections[1];
    if (!t1 || !t2 || t2 === 'Other') return null;
    const t2data = TAXONOMY[t1]?.[t2];
    if (!t2data || Array.isArray(t2data)) return null;
    const keys = Object.keys(t2data);
    if (keys.length === 0) return null;
    return ['', ...keys];
  }

  function getTier4Options() {
    const t1 = selections[0], t2 = selections[1], t3 = selections[2];
    if (!t1 || !t2 || !t3 || t3 === 'Other') return null;
    const t3data = TAXONOMY[t1]?.[t2]?.[t3];
    if (!t3data) return null;
    if (!Array.isArray(t3data)) {
      const keys = Object.keys(t3data);
      return keys.length ? ['', ...keys] : null;
    }
    if (t3data.length === 0) return null;
    return ['', ...t3data];
  }

  function getTier5Options() {
    const t1 = selections[0], t2 = selections[1], t3 = selections[2], t4 = selections[3];
    if (!t1 || !t2 || !t3 || !t4 || t4 === 'Other') return null;
    const t4data = TAXONOMY[t1]?.[t2]?.[t3]?.[t4];
    if (!Array.isArray(t4data) || t4data.length === 0) return null;
    return ['', ...t4data];
  }

  function handleSelect(tier, value) {
    const newSel = [...selections];
    const newCus = [...customs];
    newSel[tier] = value;
    newCus[tier] = '';
    for (let i = tier + 1; i < 5; i++) { newSel[i] = ''; newCus[i] = ''; }
    setSelections(newSel);
    setCustoms(newCus);
    setTeamSel('');
  }

  function handleCustom(tier, value) {
    const newCus = [...customs];
    newCus[tier] = value;
    setCustoms(newCus);
  }

  useEffect(() => {
    const main = selections[0] === 'Other'
      ? (customs[0]?.trim() || '')
      : selections[0] || '';
    const sub = buildGenreString(
      selections.slice(1).filter((_, i) => selections[i] !== ''),
      customs.slice(1),
      teamSel
    );
    onChange({ main, sub });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, customs, teamSel]);

  const tier2Opts = getTier2Options();
  const tier3Opts = getTier3Options();
  const tier4Opts = getTier4Options();
  const tier5Opts = getTier5Options();

  // Team dropdown shown when 'Specific Team' is selected at tier 5
  const showTeamPicker = selections[4] === 'Specific Team' && TEAM_LISTS[selections[3]];

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: '500' }}>
        Genre <span style={{ color: '#dc3545' }}>*</span>
        <span style={{ fontWeight: '400', color: '#888', fontSize: '0.82rem', marginLeft: '0.4rem' }}>
          — choose one or more tiers for better discoverability
        </span>
      </label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-start' }}>

        {/* Tier 1 — required */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <select
            style={selectStyle}
            value={selections[0]}
            onChange={e => handleSelect(0, e.target.value)}
            required
          >
            <option value="">Select category…</option>
            {TOP_LEVEL.map(g => <option key={g} value={g}>{g}</option>)}
            <option value="Other">Other</option>
          </select>
          {selections[0] === 'Other' && (
            <input
              style={inputStyle}
              placeholder="Enter custom category"
              value={customs[0]}
              onChange={e => handleCustom(0, e.target.value)}
            />
          )}
        </div>

        {/* Tier 2 */}
        {tier2Opts && selections[0] !== 'Other' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <select
              style={selectStyle}
              value={selections[1]}
              onChange={e => handleSelect(1, e.target.value)}
            >
              {tier2Opts.map(o => <option key={o} value={o}>{o || '— optional subcategory —'}</option>)}
            </select>
            {selections[1] === 'Other' && (
              <input
                style={inputStyle}
                placeholder="Enter custom subcategory"
                value={customs[1]}
                onChange={e => handleCustom(1, e.target.value)}
              />
            )}
          </div>
        )}

        {/* Tier 3 */}
        {tier3Opts && selections[1] && selections[1] !== 'Other' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <select
              style={selectStyle}
              value={selections[2]}
              onChange={e => handleSelect(2, e.target.value)}
            >
              {tier3Opts.map(o => <option key={o} value={o}>{o || '— optional —'}</option>)}
            </select>
            {selections[2] === 'Other' && (
              <input
                style={inputStyle}
                placeholder="Enter custom subcategory"
                value={customs[2]}
                onChange={e => handleCustom(2, e.target.value)}
              />
            )}
          </div>
        )}

        {/* Tier 4 */}
        {tier4Opts && selections[2] && selections[2] !== 'Other' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <select
              style={selectStyle}
              value={selections[3]}
              onChange={e => handleSelect(3, e.target.value)}
            >
              {tier4Opts.map(o => <option key={o} value={o}>{o || '— optional —'}</option>)}
            </select>
            {selections[3] === 'Other' && (
              <input
                style={inputStyle}
                placeholder="Enter custom subcategory"
                value={customs[3]}
                onChange={e => handleCustom(3, e.target.value)}
              />
            )}
          </div>
        )}

        {/* Tier 5 */}
        {tier5Opts && selections[3] && selections[3] !== 'Other' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <select
              style={selectStyle}
              value={selections[4]}
              onChange={e => handleSelect(4, e.target.value)}
            >
              {tier5Opts.map(o => <option key={o} value={o}>{o || '— optional —'}</option>)}
            </select>
            {selections[4] === 'Other' && (
              <input
                style={inputStyle}
                placeholder="Enter custom subcategory"
                value={customs[4]}
                onChange={e => handleCustom(4, e.target.value)}
              />
            )}
            {showTeamPicker && (
              <select
                style={{ ...selectStyle, minWidth: '220px' }}
                value={teamSel}
                onChange={e => setTeamSel(e.target.value)}
              >
                <option value="">— select team —</option>
                {TEAM_LISTS[selections[3]].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Preview */}
      {selections[0] && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#555' }}>
          <span style={{ color: '#aaa' }}>Selected: </span>
          {buildGenreString(
            selections.filter(Boolean),
            customs,
            teamSel
          ) || (selections[0] === 'Other' ? customs[0] || '…' : selections[0])}
        </div>
      )}
    </div>
  );
}
